import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ec80005d-fba4-4969-be74-853cc224eb78/artifacts/7f8it9m3_spanski-law-logo.webp';

const Header = ({ scrollY }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    if (scrollY > lastScrollY && scrollY > 100) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
    setLastScrollY(scrollY);
  }, [scrollY, lastScrollY]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const navLinks = isHomePage ? [
    { name: 'Services', href: '/lady-bird-deed-florida', isLink: true },
    { name: 'How It Works', href: '#how-it-works' },
    { name: 'About', href: '#about' },
    { name: 'FAQ', href: '#faq' },
    { name: 'Blog', href: '/blog', isLink: true },
  ] : [
    { name: 'Home', href: '/', isLink: true },
    { name: 'Services', href: '/lady-bird-deed-florida', isLink: true },
    { name: 'Blog', href: '/blog', isLink: true },
    { name: 'Contact', href: '/#contact', isLink: true },
  ];

  const scrollToSection = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementPosition - offset,
          behavior: 'smooth'
        });
      }
    }
    setIsMenuOpen(false);
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrollY > 50 ? 'glass py-3' : 'bg-transparent py-5'
        } ${isVisible ? 'translate-y-0' : '-translate-y-full'}`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <nav className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="relative z-[60]">
              <img 
                src={LOGO_URL} 
                alt="Spanski Law PLLC - Florida Lady Bird Deed Attorney" 
                className="h-12 md:h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              <a 
                href="tel:941-206-2223" 
                className="flex items-center gap-2 font-sans text-sm text-[#c9a87c] hover:text-[#d4b896] transition-colors duration-300"
              >
                <Phone className="w-4 h-4" />
                (941) 206-2223
              </a>
              <span className="w-px h-4 bg-[#c9a87c]/30" />
              {navLinks.map((link) => (
                link.isLink ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="font-sans text-sm tracking-wide text-[#faf9f7]/80 hover:text-[#c9a87c] transition-colors duration-300 link-underline"
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="font-sans text-sm tracking-wide text-[#faf9f7]/80 hover:text-[#c9a87c] transition-colors duration-300 link-underline"
                  >
                    {link.name}
                  </a>
                )
              ))}
              <a
                href="#contact"
                onClick={(e) => scrollToSection(e, '#contact')}
                className="btn-elegant px-6 py-2.5 bg-[#c9a87c] text-[#0a0a0a] font-sans text-sm font-medium tracking-wide rounded-sm hover:bg-[#d4b896]"
              >
                Free Consultation
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden relative z-[60] p-2 text-[#faf9f7]"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Navigation - Outside header for full screen overlay */}
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[55] bg-[#0a0a0a]">
          <div className="flex flex-col items-center justify-center h-full gap-6">
            {navLinks.map((link, index) => (
              link.isLink ? (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="font-serif text-3xl text-[#faf9f7] hover:text-[#c9a87c] transition-colors duration-300 animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {link.name}
                </Link>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="font-serif text-3xl text-[#faf9f7] hover:text-[#c9a87c] transition-colors duration-300 animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {link.name}
                </a>
              )
            ))}
            <Link
              to="/#contact"
              onClick={() => setIsMenuOpen(false)}
              className="mt-4 btn-elegant px-8 py-3 bg-[#c9a87c] text-[#0a0a0a] font-sans font-medium tracking-wide rounded-sm animate-fade-in-up"
              style={{ animationDelay: '0.5s' }}
            >
              Free Consultation
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
